import clsx from "clsx";
import React, { FunctionComponent, useMemo } from "react";

import {
  DealerGroupDealerListPayload,
  useGetDealerGroupDealerList,
} from "../../queries/useGetDealerGroupDealerList";
import { useNullableGroupAccountInfo } from "../../stores/hooks/useNullableGroupAccountInfo";
import { VehicleManufacturer } from "../../stores/slices/groupAccountInfoSlice";
import { OemLogo } from "./OemLogo";

const pickManufacturers = (
  rows: VehicleManufacturer[],
  dealerList: DealerGroupDealerListPayload
): VehicleManufacturer[] => {
  const validPairs = new Set(
    dealerList.map(
      ({ dealerNationalAccountName, nationalAccountId }) =>
        `${dealerNationalAccountName}|${nationalAccountId}`
    )
  );
  const picked = new Map<string, VehicleManufacturer>();
  for (const row of rows) {
    const existing = picked.get(row.manufacturerId);
    if (!existing) {
      picked.set(row.manufacturerId, row);
      continue;
    }
    const existingValid = validPairs.has(
      `${existing.manufacturerName}|${existing.nationalAccountId}`
    );
    if (existingValid) {
      continue;
    }
    const candidateValid = validPairs.has(
      `${row.manufacturerName}|${row.nationalAccountId}`
    );
    if (candidateValid) {
      picked.set(row.manufacturerId, row);
    }
  }
  return rows.filter((row) => picked.get(row.manufacturerId) === row);
};

export const OemLogos: FunctionComponent = () => {
  const groupInfo = useNullableGroupAccountInfo();
  const { data = [] } = useGetDealerGroupDealerList();
  const manufacturers = useMemo(
    () =>
      groupInfo
        ? pickManufacturers(
            groupInfo.dealerGroupPrimaryVehicleManufacturerList,
            data
          )
        : [],
    [groupInfo, data]
  );
  if (!groupInfo) {
    return null;
  }
  return (
    <section
      className={clsx(
        "my-4 mx-auto bg-paper rounded-md flex overflow-x-auto gap-0",
        "md:my-6 md:gap-2 md:bg-transparent md:rounded-none md:overflow-x-visible md:flex-wrap md:justify-center",
        "xl:max-w-7xl"
      )}
    >
      {manufacturers.map((vehicleManufacturer) => (
        <OemLogo
          dealerList={data}
          key={vehicleManufacturer.manufacturerId}
          vehicleManufacturer={vehicleManufacturer}
        />
      ))}
    </section>
  );
};

/**
 * @see docs/worklog/TW-931-dealer-group-brand-mismatch/rfc.md
 */
